import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Button, Heading, Icon, Badge } from "@elaniintc/goes-design-system";
import {
  useGetConfirmAssociationDetail,
  useDecideAssociation,
  type ApplicantProfile,
  type AssociationDocument,
} from "@/queries/confirm-association/confirm-association.api";
import { ConfirmationModalBase } from "@/components/common/modals";
import { getRequestsVisibilityLabel } from "@/utils/requests-visibility";
import { Renderify } from "renderify-js";

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getReassignPermissionLabel = (canReassign?: boolean | null) => {
  if (typeof canReassign !== "boolean") {
    return undefined;
  }

  return canReassign
    ? "Puede reasignar solicitudes a otros representantes."
    : "No puede reasignar solicitudes.";
};

const PersonCard = ({
  name,
  email,
  documentNumber,
  badge,
  vigencia,
  requestsVisibilityLabel,
  reassignPermissionLabel,
}: {
  name: string;
  email: string | null;
  documentNumber: string;
  badge?: React.ReactNode;
  vigencia?: string;
  requestsVisibilityLabel?: string;
  reassignPermissionLabel?: string;
}) => (
  <div className="border border-neutral-quiet rounded-lg overflow-hidden">
    <div className="flex items-center gap-2 p-3 bg-neutral-background">
      <div className="size-10 rounded-full bg-action-quiet/10 border-[0.5px] border-action-quiet flex items-center justify-center shrink-0">
        <Icon type="fas" name="user" className="text-base text-action-quiet" />
      </div>
      <div>
        <p className="text-base font-semibold text-body-loud">{name}</p>
        {badge}
      </div>
    </div>
    <div className="p-3 border-t border-neutral-quiet bg-neutral-background">
      {email && (
        <div className="flex items-center gap-3 py-1">
          <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
            <Icon
              type="far"
              name="envelope"
              className="text-sm text-body-quiet"
            />
          </div>
          <p className="text-sm text-body-loud">{email}</p>
        </div>
      )}
      <div className="flex items-center gap-3 py-1 bg-neutral-background">
        <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
          <Icon type="far" name="id-card" className="text-sm text-body-quiet" />
        </div>
        <p className="text-sm text-body-loud">{documentNumber}</p>
      </div>
      {vigencia !== undefined && (
        <div className="flex items-center gap-3 py-1 bg-neutral-background">
          <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
            <Icon
              type="far"
              name="calendar-times"
              className="text-sm text-body-quiet"
            />
          </div>
          <p className="text-sm text-body-loud">Vigencia: {vigencia}</p>
        </div>
      )}
      {requestsVisibilityLabel && (
        <div className="flex items-center gap-3 py-1 bg-neutral-background">
          <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
            <Icon type="far" name="eye" className="text-sm text-body-quiet" />
          </div>
          <p className="text-sm text-body-loud">{requestsVisibilityLabel}</p>
        </div>
      )}
      {reassignPermissionLabel && (
        <div className="flex items-center gap-3 py-1 bg-neutral-background">
          <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
            <Icon
              type="far"
              name="arrow-right-arrow-left"
              className="text-sm text-body-quiet"
            />
          </div>
          <p className="text-sm text-body-loud">{reassignPermissionLabel}</p>
        </div>
      )}
    </div>
  </div>
);

const PersonInfo = ({
  label,
  person,
  vigencia,
  requestsVisibilityLabel,
  reassignPermissionLabel,
}: {
  label: string;
  person: ApplicantProfile;
  vigencia?: string;
  requestsVisibilityLabel?: string;
  reassignPermissionLabel?: string;
}) => (
  <div className="mb-3">
    <p className="text-sm font-semibold text-body-loud mb-3">{label}</p>
    <PersonCard
      name={`${person.first_name} ${person.last_name ?? ""}`.trim()}
      email={person.email}
      documentNumber={person.document_number}
      vigencia={vigencia}
      requestsVisibilityLabel={requestsVisibilityLabel}
      reassignPermissionLabel={reassignPermissionLabel}
      badge={
        <Badge size="xs" variant="success">
          Activo
        </Badge>
      }
    />
  </div>
);

const UnregisteredPersonInfo = ({
  label,
  data,
  vigencia,
  requestsVisibilityLabel,
  reassignPermissionLabel,
}: {
  label: string;
  data: ApplicantProfile;
  vigencia?: string;
  requestsVisibilityLabel?: string;
  reassignPermissionLabel?: string;
}) => (
  <div className="mb-3">
    <p className="text-sm font-semibold text-body-loud mb-3">{label}</p>
    <PersonCard
      name={`${data.first_name} ${data.last_name}`}
      email={data.email}
      documentNumber={data.document_number}
      vigencia={vigencia}
      requestsVisibilityLabel={requestsVisibilityLabel}
      reassignPermissionLabel={reassignPermissionLabel}
      badge={
        <Badge size="xs" variant="warning" className="px-1.5!">
          <Icon
            type="fas"
            name="triangle-exclamation"
            className="text-xs text-warning-quiet"
          />
          No posee cuenta
        </Badge>
      }
    />
  </div>
);

const DocumentItem = ({ doc }: { doc: AssociationDocument }) => {
  return (
    <div className="flex items-center gap-3 p-3 border border-neutral rounded-lg bg-neutral-object ">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
          <Icon type="fas" name="file" className="text-sm text-body-quiet" />
        </div>
        <p className="text-sm text-body truncate min-w-0">{doc.name}</p>
      </div>

      <div className="flex gap-2 items-center shrink-0">
        <Badge size="xs" variant="neutral" className="rounded-full!">
          <p> {doc.size && formatFileSize(doc.size)}</p>
        </Badge>
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 px-2 py-1 text-body-loud hover:text-action"
        >
          <Icon type="far" name="arrow-up-right" className="text-sm" />
        </a>
      </div>
    </div>
  );
};

export const ConfirmAssociationDetail = () => {
  const params = useParams<{
    procedureId: string;
    nodeExecutionId: string;
  }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useGetConfirmAssociationDetail(
    params.nodeExecutionId ?? "",
  );

  const detail = data?.data;
  const decideMutation = useDecideAssociation(params.nodeExecutionId ?? "");
  const normalizedStatus = detail?.authorization_status?.toUpperCase();
  const isCanceled =
    detail?.formality_request?.status?.toUpperCase() === "CANCELED";
  // Resuelto por el sistema: no se muestran los botones de decisión manual
  const isResolvedBySystem = Boolean(detail?.resolved_by_system);
  // Aprobación automática: tiene su propio badge en lugar del de estado
  const isAutoApproved =
    isResolvedBySystem && normalizedStatus === "ACTIVE" && !isCanceled;
  const isDecided =
    normalizedStatus === "ACTIVE" ||
    normalizedStatus === "REJECTED" ||
    isCanceled;
  const [confirmModal, setConfirmModal] = useState<"approve" | "reject" | null>(
    null,
  );

  // 401/403 = usuario no pertenece al grupo de aprobación
  const errorStatus = (error as { response?: { status?: number } })?.response
    ?.status;
  const isUnauthorized =
    isError && (errorStatus === 401 || errorStatus === 403);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icon
          type="far"
          name="spinner-third"
          className="animate-spin text-2xl text-body"
        />
      </div>
    );
  }

  if (isUnauthorized) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Icon
          type="far"
          name="circle-exclamation"
          className="text-danger text-4xl"
        />
        <p className="text-body-quiet text-center">
          No tienes asignado este formulario
        </p>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Icon
          type="far"
          name="circle-exclamation"
          className="text-warning text-4xl"
        />
        <p className="text-body-quiet text-center">
          No se pudo cargar la información de la asociación.
        </p>
      </div>
    );
  }

  let confirmationDescription = "";
  const confirmationDescriptionUnregistered = `${detail.target_data?.first_name} ${detail.target_data?.last_name}`;
  const confirmationDescriptionRegistered =
    detail.representative?.email || "este usuario";
  if (detail.target_is_unregistered) {
    confirmationDescription = confirmationDescriptionUnregistered;
  } else {
    confirmationDescription = confirmationDescriptionRegistered;
  }

  const requestsVisibilityLabel = getRequestsVisibilityLabel(
    detail.requests_visibility,
  );

  const reassignPermissionLabel = getReassignPermissionLabel(
    detail.can_reassign,
  );

  const renderFooterContent = () => {
    if (isAutoApproved) {
      return (
        <Badge size="xs" variant="success">
          Aprobado automáticamente
        </Badge>
      );
    }

    if (isDecided) {
      return (
        <Badge
          size="xs"
          variant={normalizedStatus === "ACTIVE" ? "success" : "danger"}
        >
          {normalizedStatus === "ACTIVE" ? "Aprobada" : "Denegada"}
        </Badge>
      );
    }

    if (isResolvedBySystem) {
      return null;
    }

    return (
      <>
        <Button
          variant="danger-tertiary"
          disabled={decideMutation.isPending}
          onClick={() => setConfirmModal("reject")}
        >
          Denegar
        </Button>
        <Button
          variant="default"
          disabled={decideMutation.isPending}
          onClick={() => setConfirmModal("approve")}
        >
          Aprobar
        </Button>
      </>
    );
  };

  const footerContent = renderFooterContent();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-quiet">
        <Heading variant="h6">Revisión de asociación</Heading>
        <Button variant="ghost" size="xs">
          <Icon type="far" name="ellipsis-vertical" />
        </Button>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_4fr] lg:grid-cols-[2fr_4fr] 2xl:grid-cols-[2fr_4fr] gap-8 p-6">
          {/* Columna izquierda */}
          <div className="min-w-0">
            <Heading variant="h5" className="mb-2">
              Información del usuario asociado
            </Heading>
            {detail.description && (
              <p className="text-sm text-body">{detail.description}</p>
            )}
          </div>

          {/* Columna derecha */}
          <div className="min-w-0">
            <PersonInfo label="Usuario titular" person={detail.represented} />
            <Renderify
              condition={detail.target_is_unregistered}
              elseShow={
                <Renderify
                  condition={!!detail.representative || !!detail.target_data}
                  elseShow={null}
                >
                  <PersonInfo
                    label="Usuario a asociar"
                    person={detail.representative ?? detail.target_data!} //NOSONAR: the condition above ensures that representative is not null
                    requestsVisibilityLabel={requestsVisibilityLabel}
                    reassignPermissionLabel={reassignPermissionLabel}
                    vigencia={
                      detail.authorization_ended_at
                        ? new Date(
                            detail.authorization_ended_at,
                          ).toLocaleDateString("es-SV", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            timeZone: "America/El_Salvador",
                          })
                        : "Indeterminada"
                    }
                  />
                </Renderify>
              }
            >
              <UnregisteredPersonInfo
                label="Usuario a asociar"
                data={detail.target_data!} //NOSONAR: the condition above ensures that target_data is not null
                requestsVisibilityLabel={requestsVisibilityLabel}
                reassignPermissionLabel={reassignPermissionLabel}
                vigencia={
                  detail.authorization_ended_at
                    ? new Date(
                        detail.authorization_ended_at,
                      ).toLocaleDateString("es-SV", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        timeZone: "America/El_Salvador",
                      })
                    : "Indeterminada"
                }
              />
            </Renderify>

            {/* Documentos adjuntos */}
            {detail.documents && detail.documents.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-body-loud mb-3">
                  Carga documental
                </p>
                <div className="flex flex-col gap-2">
                  {detail.documents.map((doc) => (
                    <DocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer con botones */}
      {footerContent && (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-quiet">
          {footerContent}
        </div>
      )}

      {/* Modal de confirmación — Aprobar */}
      <ConfirmationModalBase
        isOpen={confirmModal === "approve"}
        title="Aprobar asociación"
        description={`¿Confirmas una asociación con ${confirmationDescription} para generar operaciones?`}
        acceptText="Confirmar"
        cancelText="Cancelar"
        isLoading={decideMutation.isPending}
        onAccept={() =>
          decideMutation.mutate(
            { action: "approve" },
            {
              onSuccess: () => {
                setConfirmModal(null);
                navigate(`/tramite/${params.procedureId}`);
              },
            },
          )
        }
        onClose={() => setConfirmModal(null)}
      />

      {/* Modal de confirmación — Denegar */}
      <ConfirmationModalBase
        isOpen={confirmModal === "reject"}
        title="Denegar asociación"
        description={`¿Rechazas la asociación de ${confirmationDescription}?`}
        acceptText="Denegar"
        cancelText="Cancelar"
        acceptVariant="danger-secondary"
        isLoading={decideMutation.isPending}
        onAccept={() =>
          decideMutation.mutate(
            { action: "reject" },
            {
              onSuccess: () => {
                setConfirmModal(null);
                navigate(`/tramite/${params.procedureId}`);
              },
            },
          )
        }
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};
